import {useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult} from "@tanstack/react-query";
import {ensureOk, request, requestJson} from "../apiUtils";

export const preferredLocaleKey = ["preferred-locale"] as const;

type PreferredLocaleResponse = {
    preferredLocale: string;
};

export function usePreferredLocale(): UseQueryResult<PreferredLocaleResponse> {
    return useQuery({
        queryKey: preferredLocaleKey,
        queryFn: () => requestJson<PreferredLocaleResponse>("/settings/locale"),
    });
}

type ChangeLanguagePayload = {
    preferredLocale: string;
};

export function useChangeLanguage(): UseMutationResult<void, Error, ChangeLanguagePayload> {
    const queryClient = useQueryClient();

    return useMutation({
        onMutate: async ({preferredLocale}) => {
            await queryClient.cancelQueries({queryKey: preferredLocaleKey});
            const previous = queryClient.getQueryData<PreferredLocaleResponse>(preferredLocaleKey);
            //we use optimistic updates here for better performance
            queryClient.setQueryData(preferredLocaleKey, {preferredLocale});
            return {previous};
        },
        mutationFn: async ({preferredLocale}) => {
            const response = await request("/settings/locale", {
                method: "PATCH",
                body: JSON.stringify({preferredLocale}),
            });
            await ensureOk(response);
        },
        onError: (_error, _variables, context) => {
            if (context?.previous) {
                //revert the optimistic update if error
                queryClient.setQueryData(preferredLocaleKey, context.previous);
            }
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({queryKey: preferredLocaleKey});
        },
    });
}

export type ChangePasswordPayload = {
    oldPassword: string;
    newPassword: string;
};

export function useChangePassword(): UseMutationResult<void, Error, ChangePasswordPayload> {
    return useMutation({
        mutationFn: async ({oldPassword, newPassword}) => {
            const response = await request("/settings/change-password", {
                method: "PUT",
                body: JSON.stringify({oldPassword, newPassword}),
            });
            await ensureOk(response);
        },
    });
}
